import { useEffect, useState } from 'react';
import {
  SecondaryButton,
  TertiaryButton,
} from '@JohnLewisPartnership/jl-design-system/elements/button/Button';
import classnames from 'classnames';

import {
  dispatchImmediateAnalyticsEvent,
  COMPARE_PRODUCTS,
} from '../../analytics/analyticsEvents';
import { useCompareContext } from '../../../context/PlpContext';
import {
  dispatchMonetateManagedImpressions,
  useExperiencesContext,
} from '../../experiences/Experiences';

import { ContextProps } from '../../../context/initialisers/useCompareDataInitialiser';

import { useMedia } from '../../../hooks/useMedia';
import constants from '../../../utils/constants';

import styles from './CompareBar.module.scss';

const { XXS_MOBILE_MAX_WIDTH, XS_MOBILE_MAX_WIDTH, MOBILE_MAX_WIDTH } =
  constants;

export const CompareBar = ({ removeCompareBar }) => {
  const compare: ContextProps = useCompareContext();
  const { monetateId, experiences, isCompareImpressionsFlagOn } =
    useExperiencesContext();

  const ctaStyle = useMedia(
    [
      `(min-width: ${MOBILE_MAX_WIDTH}px)`,
      `(min-width: ${XS_MOBILE_MAX_WIDTH}px)`,
      `(min-width: ${XXS_MOBILE_MAX_WIDTH}px)`,
      '(min-width: 0px)',
    ],
    ['full', 'medium', 'small', 'tiny'],
    'full'
  );

  const isXxsMobileWidth: boolean = ctaStyle === 'tiny';
  const isXsMobileWidth: boolean = ctaStyle === 'small';
  const clearButtonText: string =
    isXxsMobileWidth || isXsMobileWidth ? 'Clear' : 'Clear all';

  const comparing: number = compare.products.length;

  const inactive: boolean = !comparing || comparing === 1;

  const handleCompareClick = async (): Promise<void> => {
    await compare.load();
    compare.open();
    dispatchImmediateAnalyticsEvent(COMPARE_PRODUCTS, {
      pageInfo: 'ComparePlp',
    });
    if (isCompareImpressionsFlagOn) {
      dispatchMonetateManagedImpressions({
        monetateId,
        experiences,
        monetateFlagName: 'compareImpressionsEnabled',
      });
    }
  };

  const handleClearClick = (): void => compare.removeAll();

  const [isShown, setIsShown] = useState(false);

  useEffect(() => {
    if (comparing > 0) {
      setIsShown(true);
    } else {
      // tell parent to remove component after 300ms (to coincide with end of CSS transition)
      const timeoutId = setTimeout(() => {
        removeCompareBar();
      }, 300);
      setIsShown(false);
      return () => clearTimeout(timeoutId);
    }
  }, [comparing, removeCompareBar]);

  return (
    <div
      className={classnames(styles.container, {
        [styles.show]: isShown,
      })}
      data-testid="compare-selector"
    >
      <span className={styles.selectButtonWrapper}>
        <SecondaryButton
          className={classnames(styles.selectButton, {
            [styles.inactive]: inactive,
          })}
          data-testid="compare-btn"
          tabIndex={comparing ? 0 : -1}
          onClick={handleCompareClick}
          disabled={inactive}
        >
          {inactive
            ? 'Select another product'
            : `Compare products (${comparing} of ${compare.max})`}
        </SecondaryButton>
        <TertiaryButton
          className={classnames(styles.removeButton)}
          small={true}
          onClick={handleClearClick}
          data-testid="compare-bar-clear"
        >
          {clearButtonText}
        </TertiaryButton>
      </span>
    </div>
  );
};
